import React from 'react';
import PropTypes from 'prop-types';
import Button from './Button';

class ErrorBoundary extends React.Component {
   constructor(props) {
      super(props);
      this.state = { hasError: false, error: null, errorInfo: null };
   }

   static getDerivedStateFromError(error) {
      // Update state so the next render will show the fallback UI
      return { hasError: true };
   }

   componentDidCatch(error, errorInfo) {
      // Log error to error reporting service
      if (process.env.NODE_ENV === 'development') {
         console.error('Error caught by ErrorBoundary:', error, errorInfo);
      }

      // In production, you would send this to an error reporting service
      // Example: Sentry.captureException(error, { extra: errorInfo });

      this.setState({
         error,
         errorInfo,
      });
   }

   handleReset() {
      this.setState({ hasError: false, error: null, errorInfo: null });
   }

   render() {
      if (this.state.hasError) {
         // Custom fallback UI
         return (
            <div className="min-h-screen flex items-center justify-center bg-background--dark p-4">
               <div className="max-w-md w-full text-center space-y-6">
                  <h1 className="header-md text-body-primary--bright">
                     Something went wrong
                  </h1>
                  <p className="body text-body-secondary--bright">
                     We're sorry, but something unexpected happened. Please try refreshing the page.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                     <Button
                        label="Refresh Page"
                        onClick={() => window.location.reload()}
                        variant="primary"
                     />
                     <Button
                        label="Go Home"
                        linkTo="/"
                        variant="secondary"
                     />
                  </div>
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                     <details className="mt-8 text-left">
                        <summary className="cursor-pointer body-sm text-body-secondary--bright mb-2">
                           Error Details (Development Only)
                        </summary>
                        <pre className="bg-surface-1 p-4 rounded text-xs overflow-auto max-h-64">
                           {this.state.error.toString()}
                           {this.state.errorInfo && this.state.errorInfo.componentStack}
                        </pre>
                     </details>
                  )}
               </div>
            </div>
         );
      }

      return this.props.children;
   }
}

ErrorBoundary.propTypes = {
   children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
